import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { 
  Mail, 
  Settings,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Eye,
  EyeOff,
  Server,
  Shield,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { getUserErrorMessage } from '../../utils/errorMessages';

const AdminEmail = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [logFilter, setLogFilter] = useState('all');
  
  // Form state
  const [formData, setFormData] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: 'Spooker',
    use_ssl: false,
    use_tls: true,
    is_enabled: false
  });
  
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [logFilter]);

  const fetchData = async () => {
    try {
      const [configRes, statsRes] = await Promise.all([
        api.get('/admin/email/config'),
        api.get('/admin/email/stats')
      ]);
      
      setConfig(configRes.data);
      setStats(statsRes.data);
      
      if (configRes.data.configured) {
        setFormData({
          host: configRes.data.host || '',
          port: configRes.data.port || 587,
          username: configRes.data.username || '',
          password: '', // Don't show existing password
          from_email: configRes.data.from_email || '',
          from_name: configRes.data.from_name || 'Spooker',
          use_ssl: configRes.data.use_ssl || false,
          use_tls: configRes.data.use_tls !== false,
          is_enabled: configRes.data.is_enabled || false
        });
      }
      
      await fetchLogs();
    } catch (error) {
      toast.error('Data laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (logFilter !== 'all') {
        params.append('status', logFilter);
      }
      
      const response = await api.get(`/admin/email/logs?${params}`);
      setLogs(response.data.logs);
      setLogsTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const handleSaveConfig = async () => {
    if (!formData.host || !formData.username) {
      toast.error('Vul minimaal host en username in');
      return;
    }

    setSaving(true);
    try {
      if (config?.configured && !formData.password) {
        // Update without password
        await api.patch('/admin/email/config', {
          host: formData.host,
          port: formData.port,
          username: formData.username,
          from_email: formData.from_email || formData.username,
          from_name: formData.from_name,
          use_ssl: formData.use_ssl,
          use_tls: formData.use_tls,
          is_enabled: formData.is_enabled
        });
      } else {
        // Create new config
        if (!formData.password) {
          toast.error('Wachtwoord is vereist voor nieuwe configuratie');
          setSaving(false);
          return;
        }
        await api.post('/admin/email/config', {
          ...formData,
          from_email: formData.from_email || formData.username
        });
      }
      
      toast.success('SMTP configuratie opgeslagen');
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Opslaan mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Voer een e-mailadres in');
      return;
    }

    setTesting(true);
    try {
      await api.post('/admin/email/test', { test_email: testEmail });
      toast.success('Test email verzonden!');
      fetchLogs();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Test mislukt'));
    } finally {
      setTesting(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      sent: 'border-green-200 bg-green-50 text-green-700',
      failed: 'border-red-200 bg-red-50 text-red-700',
      logged: 'border-orange-200 bg-orange-50 text-orange-700',
      pending: 'border-neutral-200 bg-neutral-50 text-neutral-600',
    };
    const labels = {
      sent: 'Verzonden',
      failed: 'Mislukt',
      logged: 'Gelogd',
      pending: 'Wachtend',
    };
    return (
      <Badge className={variants[status] || variants.pending}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getEmailTypeBadge = (type) => {
    const labels = {
      password_reset: 'Wachtwoord Reset',
      invite: 'Uitnodiging',
      quote_status: 'Offerte Status',
      quote_review: 'Offerte Review',
      test: 'Test',
    };
    return (
      <Badge variant="outline" className="border-neutral-200 text-neutral-600">
        {labels[type] || type}
      </Badge>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">
            Admin
          </p>
          <h1 className="text-2xl font-bold tracking-tight text-black">
            Email Configuratie
          </h1>
          <p className="text-sm text-neutral-600 mt-1">
            Beheer SMTP-instellingen en bekijk verzonden emails
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{stats?.total || 0}</p>
                  <p className="text-xs text-neutral-500">Totaal emails</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{stats?.sent || 0}</p>
                  <p className="text-xs text-neutral-500">Verzonden</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center">
                  <XCircle className="h-5 w-5 text-red-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{stats?.failed || 0}</p>
                  <p className="text-xs text-neutral-500">Mislukt</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-purple-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${stats?.success_rate >= 90 ? 'text-green-600' : stats?.success_rate >= 70 ? 'text-orange-500' : 'text-red-500'}`}>
                    {stats?.success_rate || 0}%
                  </p>
                  <p className="text-xs text-neutral-500">Success rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="config" className="space-y-6">
          <TabsList>
            <TabsTrigger value="config" className="gap-2">
              <Settings className="h-4 w-4" strokeWidth={1.5} />
              Configuratie
            </TabsTrigger>
            <TabsTrigger value="logs" className="gap-2">
              <Mail className="h-4 w-4" strokeWidth={1.5} />
              Email Logs
            </TabsTrigger>
          </TabsList>

          {/* Configuration Tab */}
          <TabsContent value="config" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* SMTP Settings */}
              <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Server className="h-5 w-5 text-neutral-600" strokeWidth={1.5} />
                    SMTP Instellingen
                  </CardTitle>
                  <CardDescription>
                    Configureer uw SMTP-server voor het versturen van emails
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="host">SMTP Host</Label>
                      <Input
                        id="host"
                        value={formData.host}
                        onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                        placeholder="smtp.example.com"
                        data-testid="smtp-host-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="port">Poort</Label>
                      <Input
                        id="port"
                        type="number"
                        value={formData.port}
                        onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) || 587 })}
                        placeholder="587"
                        data-testid="smtp-port-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="username">Username / Email</Label>
                    <Input
                      id="username"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      placeholder="noreply@example.com"
                      data-testid="smtp-username-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Wachtwoord</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder={config?.configured ? "••••••••" : "SMTP wachtwoord"}
                        data-testid="smtp-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" strokeWidth={1.5} />
                        ) : (
                          <Eye className="h-4 w-4" strokeWidth={1.5} />
                        )}
                      </button>
                    </div>
                    {config?.configured && (
                      <p className="text-xs text-neutral-500">Laat leeg om bestaand wachtwoord te behouden</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="from_email">Afzender Email</Label>
                      <Input
                        id="from_email"
                        value={formData.from_email}
                        onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                        placeholder="noreply@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="from_name">Afzender Naam</Label>
                      <Input
                        id="from_name"
                        value={formData.from_name}
                        onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                        placeholder="Spooker"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between py-2 border-t border-neutral-100">
                    <div>
                      <p className="text-sm font-medium text-black">SSL Verbinding</p>
                      <p className="text-xs text-neutral-500">Gebruik SSL (poort 465)</p>
                    </div>
                    <Switch
                      checked={formData.use_ssl}
                      onCheckedChange={(checked) => setFormData({ 
                        ...formData, 
                        use_ssl: checked,
                        use_tls: checked ? false : formData.use_tls,
                        port: checked ? 465 : 587
                      })}
                    />
                  </div>

                  <div className="flex items-center justify-between py-2 border-t border-neutral-100">
                    <div>
                      <p className="text-sm font-medium text-black">STARTTLS</p>
                      <p className="text-xs text-neutral-500">Gebruik STARTTLS (poort 587)</p>
                    </div>
                    <Switch
                      checked={formData.use_tls}
                      onCheckedChange={(checked) => setFormData({ 
                        ...formData, 
                        use_tls: checked,
                        use_ssl: checked ? false : formData.use_ssl,
                        port: checked ? 587 : 465
                      })}
                    />
                  </div>

                  <div className="flex items-center justify-between py-2 border-t border-neutral-100">
                    <div>
                      <p className="text-sm font-medium text-black">Email Verzending Actief</p>
                      <p className="text-xs text-neutral-500">Schakel in om echte emails te versturen</p>
                    </div>
                    <Switch
                      checked={formData.is_enabled}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
                      data-testid="smtp-enabled-switch"
                    />
                  </div>

                  <Button 
                    className="w-full bg-black text-white hover:bg-neutral-800"
                    onClick={handleSaveConfig}
                    disabled={saving}
                    data-testid="save-smtp-config"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Configuratie Opslaan
                  </Button>
                </CardContent>
              </Card>

              {/* Test Email */}
              <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Send className="h-5 w-5 text-neutral-600" strokeWidth={1.5} />
                    Test Email
                  </CardTitle>
                  <CardDescription>
                    Verstuur een test email om de configuratie te verifiëren
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!config?.is_enabled ? (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div>
                          <p className="text-sm font-medium text-orange-800">SMTP niet actief</p>
                          <p className="text-xs text-orange-600 mt-1">
                            Schakel email verzending in om test emails te kunnen versturen.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                          <div>
                            <p className="text-sm font-medium text-green-800">SMTP Geconfigureerd</p>
                            <p className="text-xs text-green-600 mt-1">
                              {config.host}:{config.port}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="testEmail">Test E-mailadres</Label>
                        <Input
                          id="testEmail"
                          type="email"
                          value={testEmail}
                          onChange={(e) => setTestEmail(e.target.value)}
                          placeholder="uw@email.com"
                          data-testid="test-email-input"
                        />
                      </div>

                      <Button 
                        className="w-full bg-black text-white hover:bg-neutral-800"
                        onClick={handleTestEmail}
                        disabled={testing || !testEmail}
                        data-testid="send-test-email"
                      >
                        {testing ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Send className="h-4 w-4 mr-2" strokeWidth={1.5} />
                        )}
                        Test Email Versturen
                      </Button>
                    </>
                  )}

                  {/* Quick Stats */}
                  <div className="border-t border-neutral-100 pt-4">
                    <h4 className="text-sm font-medium text-black mb-3">Emails per Type</h4>
                    <div className="space-y-2">
                      {[
                        { key: 'password_reset', label: 'Wachtwoord Reset' },
                        { key: 'invite', label: 'Uitnodigingen' },
                        { key: 'quote_status', label: 'Offerte Status' },
                        { key: 'quote_review', label: 'Offerte Review' },
                        { key: 'test', label: 'Test Emails' },
                      ].map(({ key, label }) => (
                        <div key={key} className="flex items-center justify-between text-sm">
                          <span className="text-neutral-600">{label}</span>
                          <span className="font-medium text-black">{stats?.by_type?.[key] || 0}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs">
            <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                  <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                    Email Logs
                  </CardTitle>
                  <CardDescription className="text-sm mt-1">
                    {logsTotal} emails in totaal
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={logFilter} onValueChange={setLogFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle</SelectItem>
                      <SelectItem value="sent">Verzonden</SelectItem>
                      <SelectItem value="failed">Mislukt</SelectItem>
                      <SelectItem value="logged">Gelogd</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={fetchLogs}
                    className="border-neutral-200"
                  >
                    <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {logs.length > 0 ? (
                  <div className="overflow-x-auto max-h-[500px]">
                    <table className="w-full">
                      <thead className="sticky top-0 z-10 bg-white dark:bg-neutral-900">
                        <tr className="border-b border-neutral-200 dark:border-neutral-700">
                          <th className="text-left text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-3 bg-neutral-50 dark:bg-neutral-800">Ontvanger</th>
                          <th className="text-left text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-3 bg-neutral-50 dark:bg-neutral-800">Onderwerp</th>
                          <th className="text-center text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-3 bg-neutral-50 dark:bg-neutral-800">Type</th>
                          <th className="text-center text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-3 bg-neutral-50 dark:bg-neutral-800">Status</th>
                          <th className="text-right text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-3 bg-neutral-50 dark:bg-neutral-800">Datum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log, index) => (
                          <tr 
                            key={log.id} 
                            className={`border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors ${
                              index % 2 === 1 ? 'bg-neutral-50/50 dark:bg-neutral-800/20' : ''
                            }`}
                          >
                            <td className="p-3 text-sm text-neutral-900">{log.to_email}</td>
                            <td className="p-3 text-sm text-neutral-600 max-w-xs truncate">{log.subject}</td>
                            <td className="p-3 text-center">{getEmailTypeBadge(log.email_type)}</td>
                            <td className="p-3 text-center">{getStatusBadge(log.status)}</td>
                            <td className="p-3 text-right text-xs text-neutral-500">
                              {new Date(log.created_at).toLocaleString('nl-NL')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-12 text-center">
                    <Mail className="h-12 w-12 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
                    <h3 className="text-lg font-medium text-black mb-1">Geen emails gevonden</h3>
                    <p className="text-sm text-neutral-500">
                      {logFilter !== 'all' ? 'Probeer een andere filter' : 'Er zijn nog geen emails verzonden'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminEmail;
